import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, PencilSimple, Trash, Buildings, Users, EnvelopeSimple, CheckCircle, ChartBar, FileText, MagnifyingGlass, Heart, CalendarCheck, ChatCircleDots, UploadSimple, X, Clock } from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useRate } from "../context/RateContext";
import { formatMAD, convertMad } from "../lib/format";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const TYPES = ["residence", "individual", "commercial", "office", "logistics"];
const emptyProp = {
  title: { fr: "", en: "", ar: "" }, description: { fr: "", en: "", ar: "" },
  type: "residence", city: "", region: "", price_mad: 0, surface_m2: 0,
  bedrooms: 0, bathrooms: 0, images: [], features: [], featured: false,
  status: "available", delivery: "",
};

const dossierStatusColor = {
  draft: "bg-navy/10 text-navy/70",
  submitted: "bg-blue-500/20 text-blue-800",
  in_review: "bg-gold/25 text-navy",
  approved: "bg-green-500/20 text-green-800",
  rejected: "bg-red-500/20 text-red-800",
};

const Admin = () => {
  const { t, lang } = useT();
  const rate = useRate();
  const [properties, setProperties] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [team, setTeam] = useState([]);
  const [chats, setChats] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ properties: 0, users: 0, leads: 0, leads_new: 0, dossiers: 0, appointments: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/properties", { params: { sort: "recent" } }).then(({ data }) => setProperties(data));
    api.get("/admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/admin/dossiers").then(({ data }) => setDossiers(data)).catch(() => {});
    api.get("/appointments").then(({ data }) => setAppointments(data)).catch(() => {});
    api.get("/team").then(({ data }) => setTeam(data)).catch(() => {});
    api.get("/admin/chats").then(({ data }) => setChats(data)).catch(() => {});
    api.get("/contact").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return analytics;
    return analytics.filter((r) => (r.title?.[lang] || r.title?.fr || "").toLowerCase().includes(s) || (r.city || "").toLowerCase().includes(s));
  }, [analytics, q, lang]);

  const openNew = () => { setEditing({ ...emptyProp }); setDialogOpen(true); };
  const openEdit = (row) => {
    const p = propMap[row.id] || row;
    setEditing({ ...emptyProp, ...p,
      title: { ...emptyProp.title, ...p.title }, description: { ...emptyProp.description, ...p.description } });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = { ...editing };
    ["price_mad", "surface_m2", "bedrooms", "bathrooms"].forEach((k) => { payload[k] = Number(payload[k]) || 0; });
    try {
      if (editing.id) await api.put(`/properties/${editing.id}`, payload);
      else await api.post("/properties", payload);
      toast.success(editing.id ? t("admin.modified") : t("admin.added"));
      setDialogOpen(false); setEditing(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (row) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    await api.delete(`/properties/${row.id}`);
    toast.success(t("admin.deleted")); load();
  };

  const markHandled = async (id) => { await api.put(`/contact/${id}/handled`); load(); };

  return (
    <div className="relative z-10 pt-[72px] min-h-screen bg-[#F6F5F2] text-navy">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-8">
          <div>
            <span className="overline text-gold">{t("admin.dashboard")}</span>
            <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tight mt-1">{t("admin.title")}</h1>
          </div>
          <button data-testid="admin-new-property" onClick={openNew} className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-5 py-3 rounded-full hover:bg-[#C8924A] transition-colors h-fit">
            <Plus size={18} weight="bold" /> {t("admin.new_property")}
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Buildings} label={t("admin.properties")} value={stats.properties} />
          <StatCard icon={Users} label={t("admin.users")} value={stats.users} />
          <StatCard icon={EnvelopeSimple} label={t("admin.leads")} value={stats.leads} />
          <StatCard icon={FileText} label={t("admin.tab_dossiers")} value={stats.dossiers} />
          <StatCard icon={ChartBar} label={t("admin.new_leads")} value={stats.leads_new} accent />
        </div>

        <Tabs defaultValue="properties">
          <TabsList className="bg-white border border-navy/10">
            <TabsTrigger value="properties" data-testid="admin-tab-properties">{t("admin.properties")}</TabsTrigger>
            <TabsTrigger value="dossiers" data-testid="admin-tab-dossiers">{t("admin.tab_dossiers")} ({dossiers.length})</TabsTrigger>
            <TabsTrigger value="rdv" data-testid="admin-tab-rdv">{t("admin.tab_rdv")} ({appointments.length})</TabsTrigger>
            <TabsTrigger value="chats" data-testid="admin-tab-chats">{t("admin.tab_chats")} ({chats.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">{t("admin.leads")} ({leads.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="mt-6">
            <div className="relative mb-4 max-w-md">
              <MagnifyingGlass size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-navy/40" />
              <Input data-testid="admin-prop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.search_ph")} className="ps-9 bg-white border-navy/15 text-navy h-11" />
            </div>
            <div className="text-xs text-navy/40 mb-2">{filtered.length} {t("admin.showing")}</div>
            <div className="border border-navy/10 rounded-xl overflow-hidden">
              <div className="max-h-[70vh] overflow-auto">
                <table className="w-full text-sm" data-testid="admin-prop-table">
                  <thead className="sticky top-0 bg-[#ECE7DE] text-navy/60 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-start p-3 font-medium">{t("admin.bien")}</th>
                      <th className="text-start p-3 font-medium hidden md:table-cell">{t("catalog.filter_type")}</th>
                      <th className="text-end p-3 font-medium hidden lg:table-cell">{t("admin.f_price")}</th>
                      <th className="text-center p-3 font-medium"><EnvelopeSimple size={15} className="inline" /></th>
                      <th className="text-center p-3 font-medium"><Heart size={15} className="inline" /></th>
                      <th className="text-center p-3 font-medium"><CalendarCheck size={15} className="inline" /></th>
                      <th className="text-center p-3 font-medium"><FileText size={15} className="inline" /></th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-navy/10">
                    {filtered.map((r) => (
                      <tr key={r.id} data-testid={`admin-prop-${r.id}`} className="hover:bg-navy/[0.03]">
                        <td className="p-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <img src={r.image} alt="" className="w-11 h-11 object-cover rounded shrink-0" />
                            <div className="min-w-0">
                              <div className="truncate max-w-[240px]">{r.title?.[lang] || r.title?.fr}</div>
                              <div className="text-xs text-navy/40 truncate">{r.project_name?.[lang] || r.project_name?.fr} · {r.city}{r.featured && " · ★"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 hidden md:table-cell text-navy/70">{t(`types.${r.type}`)}</td>
                        <td className="p-3 text-end hidden lg:table-cell font-roboto">{r.price_on_request ? t("prop.on_request") : `${formatMAD(r.price_mad, lang)} MAD`}</td>
                        <td className="p-3 text-center font-roboto font-bold">{r.contacts}</td>
                        <td className="p-3 text-center font-roboto font-bold text-gold">{r.favorites}</td>
                        <td className="p-3 text-center font-roboto font-bold">{r.appointments}</td>
                        <td className="p-3 text-center font-roboto font-bold">{r.dossiers}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            <button data-testid={`admin-edit-${r.id}`} onClick={() => openEdit(r)} className="p-2 hover:text-gold transition-colors"><PencilSimple size={17} /></button>
                            <button data-testid={`admin-delete-${r.id}`} onClick={() => del(r)} className="p-2 hover:text-red-400 transition-colors"><Trash size={17} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="dossiers" className="mt-6">
            <div className="border border-navy/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-dossiers-table">
                <thead className="bg-[#ECE7DE] text-navy/60 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start p-3 font-medium">{t("admin.client")}</th>
                    <th className="text-start p-3 font-medium hidden md:table-cell">{t("admin.bien")}</th>
                    <th className="text-center p-3 font-medium">{t("admin.col_dossiers")}</th>
                    <th className="text-center p-3 font-medium">{t("admin.status")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy/10">
                  {dossiers.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-navy/40">—</td></tr>}
                  {dossiers.map((d) => (
                    <tr key={d.id} data-testid={`admin-dossier-${d.id}`} className="hover:bg-navy/[0.03]">
                      <td className="p-3">
                        <div>{d.user_name || "—"}</div>
                        <div className="text-xs text-navy/40">{d.user_email}</div>
                      </td>
                      <td className="p-3 hidden md:table-cell text-navy/70">{d.property_title?.[lang] || d.property_title?.fr || "—"}</td>
                      <td className="p-3 text-center font-roboto">{(d.documents || []).length}</td>
                      <td className="p-3 text-center">
                        <span className={`text-xs px-2.5 py-1 rounded-full ${dossierStatusColor[d.status] || "bg-navy/10 text-navy/60"}`}>{d.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="rdv" className="mt-6">
            <RdvPanel appointments={appointments} team={team} reload={load} t={t} lang={lang} />
          </TabsContent>

          <TabsContent value="chats" className="mt-6">
            <ChatsPanel chats={chats} />
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="border border-navy/10 divide-y divide-navy/10">
              {leads.length === 0 && <div className="p-8 text-center text-navy/40">—</div>}
              {leads.map((l) => (
                <div key={l.id} data-testid={`admin-lead-${l.id}`} className="p-4 hover:bg-navy/[0.03]">
                  <div className="flex justify-between gap-4 items-start">
                    <div>
                      <div className="font-medium">{l.name} {l.handled && <span className="text-xs text-green-400 ms-2">✓</span>}</div>
                      <div className="text-xs text-navy/50">{l.email} · {l.phone} {l.subject && `· ${l.subject}`}</div>
                    </div>
                    {!l.handled && (
                      <button data-testid={`admin-lead-handle-${l.id}`} onClick={() => markHandled(l.id)} className="text-xs text-gold inline-flex items-center gap-1 hover:underline">
                        <CheckCircle size={16} /> {t("admin.handle")}
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-navy/70 mt-2">{l.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editing && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing.id ? t("admin.edit") : t("admin.new_property")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <L label={`${t("admin.f_title")} (FR)`}><Input data-testid="form-title-fr" value={editing.title.fr} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, fr: e.target.value } })} /></L>
                <L label={`${t("admin.f_title")} (EN)`}><Input value={editing.title.en} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, en: e.target.value } })} /></L>
                <L label={`${t("admin.f_title")} (AR)`}><Input value={editing.title.ar} onChange={(e) => setEditing({ ...editing, title: { ...editing.title, ar: e.target.value } })} /></L>
              </div>
              <L label={`${t("admin.f_desc")} (FR)`}><Textarea data-testid="form-desc-fr" rows={2} value={editing.description.fr} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, fr: e.target.value } })} /></L>
              <div className="grid grid-cols-2 gap-3">
                <L label={`${t("admin.f_desc")} (EN)`}><Textarea rows={2} value={editing.description.en} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, en: e.target.value } })} /></L>
                <L label={`${t("admin.f_desc")} (AR)`}><Textarea rows={2} value={editing.description.ar} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, ar: e.target.value } })} /></L>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <L label={t("catalog.filter_type")}>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`types.${tp}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </L>
                <L label={t("admin.f_city")}><Input data-testid="form-city" value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></L>
                <L label={t("admin.f_region")}><Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} /></L>
                <L label={t("admin.f_price")}><Input data-testid="form-price" type="number" value={editing.price_mad} onChange={(e) => setEditing({ ...editing, price_mad: e.target.value })} /></L>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <L label={t("admin.f_surface")}><Input type="number" value={editing.surface_m2} onChange={(e) => setEditing({ ...editing, surface_m2: e.target.value })} /></L>
                <L label={t("admin.f_bedrooms")}><Input type="number" value={editing.bedrooms} onChange={(e) => setEditing({ ...editing, bedrooms: e.target.value })} /></L>
                <L label={t("admin.f_bathrooms")}><Input type="number" value={editing.bathrooms} onChange={(e) => setEditing({ ...editing, bathrooms: e.target.value })} /></L>
                <L label={t("admin.f_delivery")}><Input value={editing.delivery} onChange={(e) => setEditing({ ...editing, delivery: e.target.value })} /></L>
              </div>
              <ImageUploader value={editing.images || []} onChange={(imgs) => setEditing({ ...editing, images: imgs })} t={t} />
              <L label={t("admin.f_features")}><Input value={(editing.features || []).join(", ")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></L>
              <div className="flex items-center gap-3">
                <Switch data-testid="form-featured" checked={editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
                <span className="text-sm">{t("admin.f_featured")}</span>
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setDialogOpen(false)} className="px-5 py-2.5 rounded-full border text-sm">{t("admin.cancel")}</button>
              <button data-testid="form-save" onClick={save} className="px-5 py-2.5 rounded-full bg-navy text-white text-sm font-semibold">{t("admin.save")}</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const RDV_STATUS_COLOR = {
  requested: "bg-gold/20 text-navy",
  confirmed: "bg-green-500/20 text-green-800",
  rescheduled: "bg-blue-500/20 text-blue-800",
  declined: "bg-red-500/20 text-red-800",
};

const RdvPanel = ({ appointments, team, reload, t, lang }) => {
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
  const act = async (id, body) => { await api.put(`/appointments/${id}/manage`, body); toast.success("OK"); reload(); };
  return (
    <div className="border border-navy/10 rounded-xl overflow-hidden">
      <table className="w-full text-sm" data-testid="admin-rdv-table">
        <thead className="bg-[#ECE7DE] text-navy/60 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-start p-3 font-medium">{t("admin.client")}</th>
            <th className="text-start p-3 font-medium hidden md:table-cell">{t("rdv.when")}</th>
            <th className="text-start p-3 font-medium hidden lg:table-cell">{t("admin.bien")}</th>
            <th className="text-start p-3 font-medium">{t("rdv.assign")}</th>
            <th className="text-center p-3 font-medium">{t("admin.status")}</th>
            <th className="p-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-navy/10">
          {appointments.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-navy/40">—</td></tr>}
          {appointments.map((a) => (
            <tr key={a.id} data-testid={`admin-rdv-${a.id}`} className="hover:bg-navy/[0.03] align-top">
              <td className="p-3"><div>{a.user_name}</div><div className="text-xs text-navy/40">{a.user_email} · {a.subject}</div></td>
              <td className="p-3 hidden md:table-cell text-navy/70 font-roboto text-xs">{fmt(a.start)}</td>
              <td className="p-3 hidden lg:table-cell text-navy/60 text-xs">{a.property_title?.[lang] || a.property_title?.fr || "—"}</td>
              <td className="p-3">
                <select data-testid={`rdv-assign-${a.id}`} value={a.assigned_name || ""} onChange={(e) => act(a.id, { assigned_name: e.target.value })}
                  className="bg-white border border-navy/15 rounded-lg px-2 py-1.5 text-xs text-navy">
                  <option value="">{t("rdv.unassigned")}</option>
                  {team.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </td>
              <td className="p-3 text-center"><span className={`text-xs px-2.5 py-1 rounded-full ${RDV_STATUS_COLOR[a.status] || "bg-navy/10 text-navy/60"}`}>{t(`rdv.${a.status}`) || a.status}</span></td>
              <td className="p-3">
                <div className="flex flex-col gap-1.5 items-end">
                  <div className="flex gap-1.5">
                    <button data-testid={`rdv-accept-${a.id}`} onClick={() => act(a.id, { status: "confirmed" })} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-500/20 text-green-800 hover:bg-green-500/30">{t("rdv.accept")}</button>
                    <button data-testid={`rdv-decline-${a.id}`} onClick={() => act(a.id, { status: "declined" })} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-500/20 text-red-800 hover:bg-red-500/30">{t("rdv.decline")}</button>
                  </div>
                  <input data-testid={`rdv-reschedule-${a.id}`} type="datetime-local" onChange={(e) => e.target.value && act(a.id, { start: new Date(e.target.value).toISOString(), status: "rescheduled" })}
                    className="bg-white border border-navy/10 rounded-lg px-2 py-1 text-[11px] text-navy/70" title={t("rdv.reschedule")} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ChatsPanel = ({ chats }) => {
  const [open, setOpen] = useState(chats[0]?.session_id || null);
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="admin-chats">
      <div className="border border-navy/10 rounded-xl divide-y divide-navy/10 max-h-[70vh] overflow-auto">
        {chats.length === 0 && <div className="p-6 text-center text-navy/40 text-sm">—</div>}
        {chats.map((c) => (
          <button key={c.session_id} data-testid={`chat-session-${c.session_id}`} onClick={() => setOpen(c.session_id)}
            className={`w-full text-start p-3 hover:bg-navy/[0.04] ${open === c.session_id ? "bg-gold/10" : ""}`}>
            <div className="flex items-center gap-2 text-sm"><ChatCircleDots size={16} className="text-gold" /> {c.session_id.slice(0, 14)}…</div>
            <div className="text-xs text-navy/40 mt-1">{c.count} messages</div>
          </button>
        ))}
      </div>
      <div className="md:col-span-2 border border-navy/10 rounded-xl p-4 max-h-[70vh] overflow-auto">
        {(chats.find((c) => c.session_id === open)?.messages || []).map((m, i) => (
          <div key={i} className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-gold text-navy" : "bg-[#EFEBE3] text-navy"}`}>{m.text}</div>
          </div>
        ))}
        {!open && <div className="text-center text-navy/40 text-sm py-10">—</div>}
      </div>
    </div>
  );
};

const ImageUploader = ({ value, onChange, t }) => {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange([...(value || []), ...urls]);
      toast.success("OK");
    } catch (err) {
      toast.error("Échec du téléversement");
    } finally { setUploading(false); e.target.value = ""; }
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground block mb-1.5">{t("admin.f_images")}</label>
      <div className="flex flex-wrap gap-3 mb-3">
        {(value || []).map((src, i) => (
          <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-navy/10 group">
            <img src={src} alt="" className="w-full h-full object-cover" />
            <button type="button" data-testid={`img-remove-${i}`} onClick={() => remove(i)} className="absolute top-1 end-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={13} /></button>
          </div>
        ))}
      </div>
      <label data-testid="image-upload-label" className="inline-flex items-center gap-2 cursor-pointer bg-navy text-white text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-[#254B75] transition-colors">
        <UploadSimple size={17} /> {uploading ? t("admin.uploading") : t("admin.upload_images")}
        <input data-testid="form-image-input" type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
      </label>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`border p-5 ${accent ? "border-gold/40 bg-gold/5" : "border-navy/10 bg-navy/[0.03]"}`}>
    <Icon size={24} weight="light" className="text-gold mb-3" />
    <div className="font-display text-3xl">{value}</div>
    <div className="text-xs text-navy/50 uppercase tracking-wider mt-1">{label}</div>
  </div>
);

const L = ({ label, children }) => (
  <div><label className="text-xs font-medium text-muted-foreground block mb-1.5">{label}</label>{children}</div>
);

export default Admin;
